import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import logo from "@/assets/logo.png";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";

interface MobileNavProps {
  links: { href: string; label: string; isAnchor?: boolean }[];
}

const MobileNav = ({ links }: MobileNavProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden text-white hover:bg-white/10"
          aria-label="Open navigation menu"
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
        >
          <Menu className="h-6 w-6" aria-hidden="true" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[300px] bg-background border-border p-0"
        id="mobile-navigation"
        role="dialog"
        aria-modal="true"
      >
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <Link
              to="/"
              className="flex items-center gap-3"
              onClick={() => setIsOpen(false)}
              aria-label="Sports Deal Shield - Go to homepage"
            >
              <img
                src={logo}
                alt=""
                className="w-10 h-10 rounded-xl shadow-lg object-contain"
                aria-hidden="true"
              />
              <span className="font-bold text-lg text-foreground">Sports Deal Shield</span>
            </Link>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 p-4" aria-label="Mobile navigation">
            <ul className="space-y-2" role="list">
              {links.map((link) => (
                <li key={link.href}>
                  {link.isAnchor ? (
                    <a
                      href={link.href}
                      className="block py-3 px-4 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background"
                      onClick={() => setIsOpen(false)}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="block py-3 px-4 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background"
                      onClick={() => setIsOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Language, Theme Toggle & CTA Buttons */}
          <div className="p-4 border-t border-border space-y-3">
            <div className="flex items-center justify-between py-2 px-4">
              <span className="text-muted-foreground font-medium">{t('common.language')}</span>
              <LanguageSelector compact />
            </div>
            <div className="flex items-center justify-between py-2 px-4">
              <span className="text-muted-foreground font-medium">{t('common.theme')}</span>
              <ThemeToggle />
            </div>
            <Button
              variant="ghost"
              asChild
              className="w-full text-muted-foreground hover:text-foreground hover:bg-muted focus:ring-2 focus:ring-accent"
              onClick={() => setIsOpen(false)}
            >
              <Link to="/auth">{t('common.signIn')}</Link>
            </Button>
            <Button
              asChild
              className="w-full bg-accent hover:bg-accent/90 text-accent-foreground focus:ring-2 focus:ring-accent focus:ring-offset-2"
              onClick={() => setIsOpen(false)}
            >
              <Link to="/auth">{t('common.getStarted')}</Link>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNav;
